<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import KanbanCard from './KanbanCard.vue';
import { computeDropPosition } from './helpers/dragHelper';

const props = defineProps({
  list: { type: Object, required: true },
  isFinal: { type: Boolean, default: false },
});

const { t } = useI18n();
const store = useStore();
// Batch archiving is a bulk mutation, so it is admin-only like every mutating control in the
// kanban settings screens.
const { isAdmin } = useAdmin();
const getCardsByList = useMapGetter('kanban/cards/getCardsByList');

const cards = computed(() => getCardsByList.value(props.list.id));
const isArchiving = ref(false);
const archiveDialogRef = ref(null);

const handleDragOverCard = index => event => {
  event.preventDefault();
  event.currentTarget.dataset.dropIndex = index;
};

const handleDrop = async event => {
  event.preventDefault();
  const cardId = Number(
    event.dataTransfer.getData('application/x-kanban-card-id')
  );
  if (!cardId) return;

  const dropTarget = event.target.closest('[data-drop-index]');
  const dropIndex = dropTarget
    ? Number(dropTarget.dataset.dropIndex)
    : cards.value.length;
  const position = computeDropPosition(cards.value, dropIndex);

  try {
    await store.dispatch('kanban/cards/update', {
      id: cardId,
      kanban_list_id: props.list.id,
      position,
    });
  } catch (error) {
    useAlert(t('KANBAN.CONVERSATION_PANEL.MOVE_ERROR'));
  }
};

const openArchiveConfirm = () => archiveDialogRef.value?.open();

// Archiving a whole column cannot be undone from the board, so it goes through a confirmation.
const handleArchive = async () => {
  isArchiving.value = true;
  try {
    await store.dispatch('kanban/lists/archiveCards', props.list.id);
    useAlert(t('KANBAN.LIST.ARCHIVE_API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('KANBAN.LIST.ARCHIVE_API.ERROR_MESSAGE'));
  } finally {
    isArchiving.value = false;
    archiveDialogRef.value?.close();
  }
};
</script>

<template>
  <div
    class="kanban-list flex flex-col flex-shrink-0 w-72 max-h-full rounded-xl bg-n-solid-2"
    :class="{ 'ring-1 ring-n-brand': isFinal }"
  >
    <div
      class="flex items-center justify-between gap-2 px-3 py-2 border-b border-n-weak"
    >
      <span class="truncate text-heading-4 text-n-slate-12">{{
        list.title
      }}</span>
      <span
        data-test-id="kanban-list-count"
        class="px-2 py-0.5 rounded-full bg-n-slate-3 text-xs text-n-slate-11"
      >
        {{ cards.length }}
      </span>
    </div>

    <div
      data-test-id="kanban-list-body"
      class="flex-1 flex flex-col gap-2 p-2 overflow-y-auto"
      @dragover.prevent
      @drop="handleDrop"
    >
      <div
        v-for="(card, index) in cards"
        :key="card.id"
        :data-drop-index="index"
        @dragover="handleDragOverCard(index)"
      >
        <KanbanCard :card="card" />
      </div>
    </div>

    <div v-if="isFinal && isAdmin" class="p-2 border-t border-n-weak">
      <Button
        data-test-id="kanban-archive-button"
        :label="t('KANBAN.LIST.ARCHIVE_CONCLUDED')"
        size="sm"
        faded
        slate
        class="w-full"
        :is-loading="isArchiving"
        @click="openArchiveConfirm"
      />
      <Dialog
        ref="archiveDialogRef"
        type="alert"
        :title="
          t('KANBAN.LIST.ARCHIVE_CONFIRM.TITLE', { listTitle: list.title })
        "
        :description="t('KANBAN.LIST.ARCHIVE_CONFIRM.MESSAGE')"
        :confirm-button-label="t('KANBAN.LIST.ARCHIVE_CONFIRM.YES')"
        :cancel-button-label="t('KANBAN.LIST.ARCHIVE_CONFIRM.NO')"
        :is-loading="isArchiving"
        @confirm="handleArchive"
      />
    </div>
  </div>
</template>
